import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Drawer } from '@material-tailwind/react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChosenVariantDto } from '../../constants/types';
import { useAppDispatch, useAppSelector } from '../../store/hook';
import { getShopInfo, selectShopInfo } from '../../store/features/shopSlice';
import { Options } from '../options/options';
import FullWidthPage from '../layout/FullWidthPage';
import TextWinds from '../common/TextWinds';
import Buttons from '../common/Buttons';
import { addCart } from '../../store/features/cartSlice';
import { getOrderItem } from '../../api/utils/common-purchase';
import { resetPurchaseInfo } from '../../store/features/purchaseSlice';
import { excuteAxiosPost } from '../../api/axios/call/testAxiosCall';
import useUserHook from '../../controllers/useUserHook';
import { openAlert } from '../../store/features/modalSlice';
import renderDetailContents from '../../shared/renderDetailContents';
import {
  getInterestFreeInstallmentMessage,
  INTEREST_FREE_INSTALLMENT_DEFAULT,
} from '../../utils/interest-free-installment';

const ShopDetails = () => {
  const { checkWithCallback } = useUserHook();
  const [searchParams] = useSearchParams();
  const { shopId } = useParams();
  const path = '/v1/product/' + shopId;
  const dispatch = useAppDispatch();
  const shopInfo = useAppSelector(selectShopInfo);

  useEffect(() => {
    dispatch(getShopInfo({ url: path }));
  }, [path]);

  const [openBottom, setOpenBottom] = useState(
    !!searchParams.get('optionItems'),
  );
  const openDrawerBottom = () => {
    setOpenBottom(true);
  };
  const closeDrawerBottom = () => {
    setOpenBottom(false);
  };

  const isLoading = (): boolean => {
    if (!shopInfo) return true;
    return shopInfo.id != shopId;
  };

  const infoBtnClick = async () => {
    const message = await getInterestFreeInstallmentMessage();
    dispatch(openAlert({ message }));
    // dispatch(openAlert({ message: INTEREST_FREE_INSTALLMENT_DEFAULT }));
  };

  // OPTION
  const navigate = useNavigate();
  // 1. 구매하기
  const onPurchaseHandle = async (
    childId: string,
    selectOptionData: ChosenVariantDto[],
  ) => {
    const action = async () => {
      const path = '/v1/order/template/from-prod';
      const params = {
        order_item: getOrderItem({
          product_id: childId,
          variants: selectOptionData,
          options: [],
          enrolls: [],
        }),
      };
      await Promise.all([excuteAxiosPost(path, params)]).then((res) => {
        if (res) {
          dispatch(resetPurchaseInfo());
          navigate(
            { pathname: '/purchase' },
            { state: { purchaseId: res[0].data.id } },
          );
        }
      });
    };
    checkWithCallback(action);
  };

  // 2. 장바구니
  const onCartHandle = useCallback(
    async (childId: string, selectItem: ChosenVariantDto[]) => {
      const action = async () => {
        if (selectItem.length > 0) {
          try {
            const res = await dispatch(
              addCart({
                url: '/v1/cart',
                data: {
                  order_item: getOrderItem({
                    product_id: shopInfo.id,
                    variants: selectItem,
                    options: [],
                    enrolls: [],
                  }),
                },
              }),
            ).unwrap();

            if (res) {
              navigate('/cart');
            }
          } catch (e) {
            console.error(
              '일시적인 장애로 상품을 구매할 수 없습니다. 나중에 다시 시도해주세요.',
            );
          }
        }
      };
      checkWithCallback(action);
    },
    [shopInfo],
  );

  return isLoading() ? null : (
    <FullWidthPage>
      {shopInfo && (
        <div className="flex flex-col gap-y-6 pt-10">
          <TextWinds type="title_h2" xlType="title_h1" className="px-4">
            {shopInfo.name}
          </TextWinds>
          <div className="flex flex-col gap-[32px] lg:flex-row lg:pt-[24px]">
            <div className="flex w-full shrink-0 lg:w-[275px]">
              <div className="sticky top-20 h-fit w-full bg-gray0 p-[10px]">
                <div className="relative w-full">
                  <TextWinds type="title_h6" color="gray7">
                    설명
                  </TextWinds>
                  <TextWinds type="content_rel" className="pt-[4px]">
                    {shopInfo.description}
                  </TextWinds>
                </div>
                <div className="mt-[16px] bg-white p-[10px]">
                  <div
                    className="flex cursor-pointer items-center gap-[10px] p-[10px]"
                    onClick={infoBtnClick}
                  >
                    <InformationCircleIcon className="relative h-[24px] w-[24px]" />
                    <TextWinds type="content_rel">무이자 할부 안내</TextWinds>
                  </div>
                  <div className="flex w-full flex-col items-center justify-center gap-[13px] pt-[13px]">
                    <Buttons
                      type="filled"
                      onClick={openDrawerBottom}
                      color={'purple5'}
                    >
                      구매하기
                    </Buttons>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex basis-full flex-col">
              {renderDetailContents({
                contents: [...shopInfo.contents],
                productName: shopInfo.name,
                imageAltPrefix: '상품',
              })}
            </div>
            <div
              className={`fixed bottom-0 left-0 w-full p-4 ${
                openBottom ? 'block' : 'hidden'
              }`}
            >
              <Drawer
                placement="bottom"
                open={openBottom}
                onClose={closeDrawerBottom}
                overlay={false}
                size={500}
                // className="items-center w-1/2 px-5 py-4 left-1/4 right-1/4 h-fit"
                className="relative right-1/4 m-auto w-full items-center rounded-2xl px-5 py-4 drop-shadow-xl lg:w-1/2"
              >
                <Options
                  gubun="shop"
                  shopInfo={shopInfo}
                  chosenVariants={shopInfo.option}
                  closeDrawerBottom={closeDrawerBottom}
                  onCartHandle={onCartHandle}
                  onPurchaseHandle={onPurchaseHandle}
                />
              </Drawer>
            </div>
          </div>
        </div>
      )}
    </FullWidthPage>
  );
};

export default ShopDetails;
